import React, { useEffect, useState, type ReactNode } from "react";
import U2000BasicSearch, { SEARCHTAB } from "./U2000BasicSearch";
import U2000CommonTableActions from "../commons/U2000CommonTableActions";
import U2000CommonTableFooter from "./U2000CommonTableFooter";
import U2000BasicTableList from "./U2000BasicTableList";
import type { U2000SearchStateHandlers } from "../../../reducer/formReducer/U2000SearchStateHandlers";
import type { U2000GridStateHandlers } from "../../../reducer/formReducer/U2000GridStateHandlers";
import type { U2000TableHandlers } from "../../../types/U2000TableHandlers";
import { U2000GridStateEnumPaginationDirection } from "../../../reducer/formReducer/U2000GridState";
import { u2000DefaultSortOrderForColumn } from "../../../tools/U2000DefaultSortOrderForColumn";

interface U2000BasicTableProps<T extends Record<string, any> = any> {
  searchStateHandlers: U2000SearchStateHandlers;
  gridStateHandlers: U2000GridStateHandlers<T>;
  label: string;
  actionsButtons: React.ReactNode;
  handlerActions?: U2000TableHandlers<T>;
  allowRowSelection: boolean;
  /**
   * true = barre compacte uniquement (recherche repliée, icônes pour développer).
   * false = panneau complet visible.
   * undefined = utiliser `searchStateHandlers.getHideSearch()` (initLU.hideSearch).
   */
  hideSearchPanel?: boolean;
  /** Barre compacte : contenu après les icônes (ex. préréglages). */
  searchCollapsedLeadingSlot?: ReactNode;
  /** Barre compacte : afficher les critères actifs comme puces (défaut true via BasicSearch — BL-0407). */
  showCollapsedCriteriaChips?: boolean;
  /** Après suppression d’un critère via une puce. */
  onSearchCriteriaChipRemoved?: (fieldName: string) => void;
  exportExcelTitle?: string;
  isExporting?: boolean;
  hasExportRole?: boolean;
  /** Voir `U2000CommonTableActions.extraSearchActions`. */
  extraSearchActions?: ReactNode;
  /**
   * Si défini, seules ces lignes sont concernées par la case « tout sélectionner » (en-tête) et son état coché.
   * Les autres lignes restent non cochées lors d’un « tout sélectionner ».
   */
  isRowSelectable?: (row: T) => boolean;
  /** Contenu personnalisé lorsque la grille est vide (remplace le message par défaut). */
  emptyTableBody?: ReactNode;
}

export interface U2000Pagination {
  page: number;
  size: number;
}

export interface U2000SearchPayload {
  filter: U2000SearchParameter;
  pagination: U2000Pagination;
}

export interface U2000CriteriaParameter {
  field: string;
  operator: string;
  value: any;
  type: string;
}

export interface U2000FieldParameter {
  name: string;
}

export interface U2000SortedFieldParameter {
  name: string;
  sortOrder?: string;
}

export interface U2000ColumnParameter {
  name: string;
  isDefaultColumn?: boolean;
  isDefaultSortedColumn?: boolean;
  defaultSortOrder?: "ASC" | "DESC";
  [key: string]: any;
}

export interface U2000SearchParameter {
  criterias: U2000CriteriaParameter[];
  fields: U2000FieldParameter[];
  sortedFields: U2000SortedFieldParameter[];
}

export interface U2000SearchResult<T> {
  input: U2000SearchParameter;
  pagination: U2000Pagination;
  data: T[];
  nbRecord: number;
  currentIdx: number;
}

/**
 * Composant de table générique avec recherche, pagination et actions personnalisées.
 * @template T Type des données de ligne (doit contenir un champ 'id')
 *
 * Principales fonctionnalités :
 * - Affichage d'une table avec colonnes dynamiques
 * - Recherche avancée (critères, champs, tri)
 * - Pagination et gestion du nombre d'éléments par page
 * - Sélection de lignes et actions personnalisées
 * - Synchronisation avec les données du parent via props
 */
const U2000BasicTable = <T extends Record<string, any> & { id: any }>({
  searchStateHandlers,
  gridStateHandlers,
  actionsButtons,
  label,
  handlerActions,
  allowRowSelection = true,
  hideSearchPanel,
  searchCollapsedLeadingSlot,
  showCollapsedCriteriaChips,
  onSearchCriteriaChipRemoved,
  exportExcelTitle,
  isExporting = false,
  hasExportRole = false,
  extraSearchActions,
  isRowSelectable,
  emptyTableBody,
}: U2000BasicTableProps<T> & {}) => {
  // Initialisation les selected fields et sorted fields seulement si pas recherche par défaut
  const [hasInitialized, setHasInitialized] = useState(false);
  // BL-0407 — hideSearchPanel prop explicite, sinon initLU via searchStateHandlers.getHideSearch()
  // (la plupart des listes ne passaient pas hideSearchPanel, donc hideSearch:true était ignoré).
  const hideSearchFromInit =
    hideSearchPanel !== undefined ? hideSearchPanel === true : searchStateHandlers.getHideSearch() === true;
  const [hideSearch, setHideSearch] = useState(() => hideSearchFromInit);
  const [searchTab, setSearchTab] = useState<string>(SEARCHTAB.CRITERIAS);
  // BL-0405 — jeton incrémenté au clic en-tête : la recherche est relancée au rendu suivant, quand
  // le nouveau `sortedFields` est commité (sinon `extractSearchParameter()` renvoie le tri précédent).
  const [headerSortSearchToken, setHeaderSortSearchToken] = useState(0);

  // Sync quand initLU arrive en async (ex. default hideSearch:false → API hideSearch:true)
  // ou quand la prop hideSearchPanel change. Ne pas dépendre de l’identité des handlers.
  useEffect(() => {
    setHideSearch(hideSearchFromInit);
  }, [hideSearchFromInit]);

  useEffect(() => {
    const columns = searchStateHandlers.getColumns();
    if (columns.length === 0 || hasInitialized) {
      return;
    }
    const savedFilters = searchStateHandlers.getSavedFilters && searchStateHandlers.getSavedFilters();
    if (savedFilters && !searchStateHandlers.hasAlreadyLoadedFilters) {
      const defaultFilter = savedFilters.find((c) => c.isDefaultFilter);
      if (!defaultFilter) {
        const columnsTyped = columns as U2000ColumnParameter[];
        gridStateHandlers.setSelectedFields(columnsTyped.filter((col) => col.isDefaultColumn).map((col) => col.name));
        searchStateHandlers.setSortedFields(
          columnsTyped
            .filter((col) => col.isDefaultSortedColumn)
            .map((col) => ({ field: { ...col }, sortOrder: u2000DefaultSortOrderForColumn(col) })) as any,
        );
      }
    }
    setHasInitialized(true);
  }, [searchStateHandlers, gridStateHandlers, hasInitialized]);

  // Change l'onglet actif dans la recherche
  const handleChangeTab = (tab: string) => {
    setSearchTab(tab ?? SEARCHTAB.CRITERIAS);
  };

  /**
   * Affiche ou masque le panneau de recherche selon l'onglet sélectionné
   * @param tab Onglet à afficher
   */
  const handleShowSearch = (tab: string) => {
    if (!hideSearch && tab === searchTab) {
      setHideSearch(true);
    } else {
      setHideSearch(false);
      setSearchTab(tab ?? SEARCHTAB.CRITERIAS);
    }
  };

  /** BL-0407 — replie toujours le panneau (après Rechercher / F3), sans toggle. */
  const handleCollapseSearch = () => {
    setHideSearch(true);
  };

  /**
   * Callback appelé lors d'un changement de filtre/recherche
   * Réinitialise la pagination à la première page et déclenche la recherche
   * @param searchParameter Nouveaux paramètres de recherche
   */
  function onFilterChanged() {
    if (handlerActions && typeof handlerActions.onSearch === "function") {
      handlerActions.onSearch(1, U2000GridStateEnumPaginationDirection.NEXTPAGE);
    }
  }

  /** BL-0405 — clic en-tête : applyHeaderSort puis relance page 1 via `headerSortSearchToken`. */
  function onColumnHeaderSortClick(column: Parameters<typeof searchStateHandlers.applyHeaderSort>[0]) {
    if (!searchStateHandlers.applyHeaderSort(column)) {
      return;
    }
    setHeaderSortSearchToken((token) => token + 1);
  }

  useEffect(() => {
    if (headerSortSearchToken === 0) {
      return;
    }
    if (handlerActions && typeof handlerActions.onSearch === "function") {
      handlerActions.onSearch(1, U2000GridStateEnumPaginationDirection.NEXTPAGE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- relance uniquement sur un clic en-tête, avec le tri déjà commité
  }, [headerSortSearchToken]);

  /**
   * Callback appelé lors d'un changement de page
   * Met à jour la pagination et déclenche la recherche
   * @param page Nouvelle page sélectionnée
   */
  function onPageChanged(page: number) {
    if (handlerActions && typeof handlerActions.onSearch === "function") {
      handlerActions.onSearch(page, page > gridStateHandlers.getPagination().page ? U2000GridStateEnumPaginationDirection.NEXTPAGE : U2000GridStateEnumPaginationDirection.PREVIOUSPAGE);
    }
  }

  /**
   * Sélectionne ou désélectionne toutes les lignes de la table
   * Met à jour l'état local et notifie le parent via onRowSelectedChange
   * @param isSelected true pour tout sélectionner, false pour tout désélectionner
   */
  function handleSelectAllRow(isSelected: boolean) {
    if (isRowSelectable) {
      const data = gridStateHandlers.getData();
      const updated = data.map((row) => ({
        ...row,
        idChecked: isRowSelectable(row as T) ? isSelected : false,
      }));
      gridStateHandlers.setData(updated as T[]);
    } else {
      gridStateHandlers.selectAllRow(isSelected);
    }
  }

  /**
   * Met à jour une ligne spécifique dans la table (ex: après modification d'une case à cocher)
   * Notifie le parent si la sélection change
   * @param row Ligne modifiée (doit contenir id)
   */
  function rowUpdated(row: T & { id?: any; idChecked?: boolean }) {
    if (row) {
      gridStateHandlers.selectRow(row.id, row.idChecked as boolean);
    }
  }

  if (!hasInitialized) return;

  // Rendu du composant : panneau de recherche, actions, table et pied de page
  return (
    <main className="w-full px-4 sm:px-6 lg:px-8 py-3">
      {/* Panneau de recherche avancée (barre compacte visible lorsque le panneau est réduit) */}
      <section id="u2000-basic-search-section" className={`form-card animate-fade-in ${hideSearch ? "mb-4" : "mb-6"}`} role="search" aria-label="Panneau de recherche avancée">
        <div className={`form-card-body ${hideSearch ? "py-3" : ""}`}>
          <U2000BasicSearch
            searchStateHandlers={searchStateHandlers}
            defaultTab={searchTab}
            handleChangeTab={handleChangeTab}
            handleShowSearch={handleShowSearch}
            onCollapseSearch={handleCollapseSearch}
            onFilterChange={onFilterChanged}
            isCollapsed={hideSearch}
            collapsedLeadingSlot={searchCollapsedLeadingSlot}
            showCollapsedCriteriaChips={showCollapsedCriteriaChips}
            onCriteriaChipRemoved={onSearchCriteriaChipRemoved}
          />
        </div>
      </section>

      {/* Table principale et actions */}
      <article className="form-card animate-fade-in w-full" role="main" aria-label="Liste des données">
        {/* Header avec actions */}
        <header className="form-card-header">
          <U2000CommonTableActions
            gridStateHandlers={gridStateHandlers}
            actionsButtons={actionsButtons}
            onPaginationChanged={onPageChanged}
            onExportExcel={handlerActions?.onExportExcel}
            exportExcelTitle={exportExcelTitle}
            isExporting={isExporting}
            hasExportRole={hasExportRole}
            extraSearchActions={extraSearchActions}
          />
        </header>

        {/* Corps de la table */}
        <section className="form-card-body px-4 py-2 mb-2 ">
          <U2000BasicTableList
            gridStateHandlers={gridStateHandlers}
            rowClickFn={handlerActions && typeof handlerActions.onRowClick === "function" ? (handlerActions.onRowClick as unknown as (row: any) => void) : undefined}
            rowUpdated={rowUpdated}
            allowRowSelection={allowRowSelection}
            handleSelectAllRow={handleSelectAllRow}
            isRowSelectable={isRowSelectable}
            emptyTableBody={emptyTableBody}
            onColumnHeaderSortClick={onColumnHeaderSortClick}
          />
        </section>

        {/* Footer avec informations */}
        <footer className="form-card-footer">
          <U2000CommonTableFooter nbItem={gridStateHandlers.getTotalRows()} label={label} />
        </footer>
      </article>
    </main>
  );
};

export default U2000BasicTable;
